import PropTypes from "prop-types";
import { useState } from "react";

import styled from "styled-components/macro";

import useColorSchemeIsLight from "Giving/ManageMyGivingPage/stateHooks/useColorSchemeIsLight";
import { motion } from "framer-motion";

import FnrIcon, { FnrIconWrapper } from "UI/FnrIcon";
import FnrTooltip from "UI/FnrTooltip2";

import { formatNumberWithCommas } from "Utilities/Number";

import DesignationLink from "./DesignationLink";
import Designations from "./Designations";
import PartiallyRefundedInfo from "./PartiallyRefundedInfo";
import PaymentMethod from "./PaymentMethod";

ListItem.propTypes = {
  amount: PropTypes.object,
  designations: PropTypes.array,
  paymentTypeIcon: PropTypes.string,
  paymentCardType: PropTypes.string,
};

export default function ListItem({
  amount,
  designations,
  showEditButtons,
  isLast,
  paymentTypeIcon,
  paymentCardType,
  dateString,
  isRecurring,
  statusInactive,
  statusText,
  isPartiallyRefunded,
  payment,
}) {
  const [showMultiDesignations, setShowMultiDesignations] = useState(false);
  const refunded = statusText === "Refunded";
  const { campaignName, fundName, fundID, campaignID, paymentsEnabled } =
    designations[0];
  const colorSchemeIsLight = useColorSchemeIsLight();

  const animationVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "tween", duration: 0.25 },
    },
  };

  return (
    <Wrapper $refunded={refunded} variants={animationVariants}>
      <Top>
        <PaymentMethod
          paymentTypeIcon={paymentTypeIcon}
          paymentCardType={paymentCardType}
        />

        <div>
          <Date>{dateString}</Date>
          <Designation
            animate={showMultiDesignations ? { opacity: 0, height: 0 } : null}
          >
            {designations.length === 1 ? (
              <DesignationTextAround>
                <DesignationLink
                  paymentsEnabled={paymentsEnabled}
                  fundID={fundID}
                >
                  <DesignationText>
                    {campaignName || fundName}
                    {campaignID && (
                      <PledgeIndicator>
                        <FnrIcon fad fa-hand-holding-usd />
                      </PledgeIndicator>
                    )}
                  </DesignationText>
                </DesignationLink>
              </DesignationTextAround>
            ) : (
              <>
                <DesignationText>{campaignName || fundName}</DesignationText>
                <MoreDesignationsButtonWrapper>
                  <MoreDesignations
                    whileHover={{ opacity: 1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowMultiDesignations(true)}
                  >
                    {` +${designations.length} more`}
                    <FnrIcon far fa-angle-down inline />
                  </MoreDesignations>
                </MoreDesignationsButtonWrapper>
              </>
            )}
          </Designation>
        </div>

        <Amount>
          {isRecurring && (
            <FnrTooltip content="Recurring Gift">
              <PaymentInfoIcon>
                <FnrIcon fad fa-repeat />
              </PaymentInfoIcon>
            </FnrTooltip>
          )}
          {refunded && (
            <FnrTooltip content="Refunded">
              <PaymentInfoIcon>
                <FnrIcon fad fa-undo />
              </PaymentInfoIcon>
            </FnrTooltip>
          )}
          {isPartiallyRefunded && (
            <FnrTooltip content={<PartiallyRefundedInfo payment={payment} />}>
              <PaymentInfoIcon>
                <FnrIcon far fa-envelope-open-dollar />
              </PaymentInfoIcon>
            </FnrTooltip>
          )}

          <TotalNumber>
            <WholeNumber>{formatNumberWithCommas(amount.whole)}</WholeNumber>
            <DecimalNumber>{amount.decimal}</DecimalNumber>
            {refunded || statusInactive ? (
              <Strikethrough $colorSchemeIsLight={colorSchemeIsLight} />
            ) : null}
          </TotalNumber>
        </Amount>
      </Top>
      {designations.length > 1 && (
        <Designations
          designations={designations}
          show={showMultiDesignations}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  opacity: ${({ $refunded }) => ($refunded ? "0.5" : "1")};
  padding: 4px 0;
  padding-bottom: 12px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 20px;
  padding-bottom: 0;
`;

const Date = styled.div`
  font-size: 18px;
  line-height: 18px;
`;
const Amount = styled.div`
  text-align: right;
  font-size: 24px;
  font-weight: 400;
  position: relative;
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const TotalNumber = styled.span`
  display: flex;
  align-items: flex-start;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  position: relative;
`;

const WholeNumber = styled.span`
  font-size: 20px;
  line-height: 20px;
  &:before {
    content: "$";
  }
`;

const DecimalNumber = styled.span`
  font-size: 12px;
  line-height: 12px;
  margin-left: 1px;
`;
const Designation = styled(motion.div)`
  font-size: 14px;
  overflow: hidden;
`;

const PaymentInfoIcon = styled.div`
  margin-right: 4px;
  font-size: 13px;
`;
const PledgeIndicator = styled.div`
  margin-left: 4px;
  font-size: 10px;
  display: inline-block;
  position: relative;
  & ${FnrIconWrapper} {
    position: absolute;
    margin-top: -12px;
  }
`;
const MoreDesignationsButtonWrapper = styled.div`
  display: flex;
`;
const MoreDesignations = styled(motion.div)`
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.7;
  cursor: pointer;
  & ${FnrIconWrapper} {
    margin-left: 4px;
    font-size: 12px;
  }
`;

const DesignationTextAround = styled.div`
  display: flex;
`;
const DesignationText = styled.div`
  opacity: 0.75;
  transition: opacity 0.2s, transform 0.2s;
  A:hover & {
    opacity: 1;
  }
  A:active & {
    scale: 0.96;
  }
`;

const Strikethrough = styled.div`
  border-bottom: 3px solid
    ${({
      $colorSchemeIsLight,
      theme: {
        colors: { textBlack, textLight },
      },
    }) => ($colorSchemeIsLight ? textBlack : textLight)};
  width: 100%;
  height: 9px;
  position: absolute;
`;
